import { createSelector } from "reselect";
import type { RootState } from "@/core/store";
import type { MenuProduct } from "@/types/menu-product";

// Basic state selectors
export const selectOrdersState = (state: RootState) => state.orders;
export const selectCurrentOrder = (state: RootState) =>
  state.orders.currentOrder;
export const selectOrdersLoading = (state: RootState) => state.orders.loading;
export const selectOrdersError = (state: RootState) => state.orders.error;

// Cart-specific selectors
export const selectCartItems = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.items
);

export const selectCartTotal = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.priceTotal
);

export const selectCartItemsCount = createSelector(
  [selectCartItems],
  (items) => items.length
);

export const selectCartComment = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.comment || ""
);

// Cart statistics selector
export const selectCartStats = createSelector(
  [selectCartItems, selectCartTotal],
  (items, total) => ({
    itemCount: items.length,
    totalSum: total,
    isEmpty: items.length === 0,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    averageItemPrice:
      items.length > 0
        ? total / items.reduce((sum, item) => sum + item.quantity, 0)
        : 0,
  })
);

// Selector to get quantity of a specific product in cart
export const selectProductQuantityInCart = createSelector(
  [selectCartItems, (_state: RootState, productId: string) => productId],
  (items, productId) => {
    const item = items.find(
      (item) => item.productSnapshot.productId === productId || item.productId === productId
    );
    return item ? item.quantity : 0;
  }
);

// Selector to get quantity of a product or its variants in cart
// Проверяет наличие основного товара и всех его вариантов в корзине
export const selectProductOrVariantQuantityInCart = createSelector(
  [selectCartItems, (_state: RootState, product: MenuProduct) => product],
  (items, product) => {
    // Идентификаторы, которыми считаем товар «этим товаром»: сам товар и
    // его варианты.
    const ids =
      product.variantGroup && product.variants && product.variants.length > 0
        ? [product.id, ...product.variants.map((variant) => variant.id)]
        : [product.id];

    // С опциями у одного товара в корзине может быть несколько позиций
    // (разные наборы опций), поэтому считаем суммой, а не первой найденной
    // (.find показал бы количество только одной позиции).
    return items
      .filter(
        (item) =>
          ids.includes(item.productSnapshot.productId) ||
          ids.includes(item.productId)
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }
);

// Legacy selectors (kept for backward compatibility)
export const selectCurrentOrderSum = selectCartTotal;
export const selectCurrentOrderItemsCount = selectCartItemsCount;


// Order sync state selectors
export const selectIsSyncing = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.isSyncing || false
);

export const selectSyncError = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.lastSyncError || null
);

export const selectPendingChanges = createSelector(
  [selectCurrentOrder],
  (currentOrder) => currentOrder.pendingChanges || false
);

// Selector for persisted order ID
export const selectPersistedOrderId = createSelector(
  [selectOrdersState],
  (ordersState) => ordersState.persistedOrderId
);

// Selector for the pre-optimistic snapshot used by rollback contract (CR-006).
export const selectPendingRollback = (state: RootState) =>
  state.orders.pendingRollback;
